import { Portal, setSelector } from '@linusborg/vue-simple-portal';
import { lock, unlock } from '@portal/utils/util-body-scroll-lock';
import { guardUnspecified } from '@portal/utils/util-guards';
import { componentFactory } from 'vue-tsx-support';

import { injectStylesMixin } from '../../mixins/inject-styles';

import styles from './jtn-ui-modal.styles.scss?module';

type ComponentData = {
  needRender: boolean;
  isScrollable: boolean;
};

export default componentFactory.mixin(injectStylesMixin(styles)).create({
  name: 'JtnUiModal',
  model: {
    prop: 'isOpen',
    event: 'toggle'
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    appendTo: {
      type: String,
      default: 'body'
    },
    targetName: {
      type: String,
      default: 'modal'
    },
    maxWidth: {
      type: Number,
      default: 600
    },
    dataTest: {
      type: String,
      default: ''
    },
    contentStyle: {
      type: String,
      default: ''
    },
    overlayStyle: {
      type: String,
      default: ''
    }
  },
  data(): ComponentData {
    return {
      needRender: false,
      isScrollable: false
    };
  },
  computed: {
    selector() {
      return `#jtn-vm-${this.targetName}`;
    }
  },
  watch: {
    isOpen(val) {
      this.$emit(val ? 'beforeOpen' : 'beforeClose');
      this.needRender = val;
    },
    needRender(val) {
      if (val) {
        document.addEventListener('keydown', this.keydownHandler);
        lock();

        this.$nextTick(() => {
          this.setScrollable();
          this.$emit('opened');
        });

        return;
      }

      document.removeEventListener('keydown', this.keydownHandler);
      unlock();

      this.$nextTick(() => {
        this.$emit('closed');
      });
    }
  },
  beforeMount() {
    setSelector(this.selector);
    this.needRender = this.isOpen;
  },
  methods: {
    close() {
      this.$emit('toggle', false);
    },
    setScrollable() {
      const modalWrap = this.$refs.modalWrap as HTMLElement;

      if (guardUnspecified(modalWrap)) {
        this.isScrollable = modalWrap.scrollHeight > modalWrap.clientHeight;
      }
    },
    keydownHandler(event: KeyboardEvent) {
      if (['Escape', 'Esc'].includes(event.key)) {
        this.close();
      }
    }
  },
  render() {
    return (
      <div>
        {this.needRender && (
          <Portal appendTo={this.appendTo} selector={this.selector}>
            <div
              class={[styles.vmWrap, this.isScrollable ? styles.scrollable : '']}
              ref="modalWrap"
              data-test={this.dataTest}
            >
              <div
                class={styles.vmOverlay}
                style={this.overlayStyle}
                onClick={() => this.close()}
                role="button"
                data-test="jtn-ui-modal-overlay"
              />
              <section
                style={{
                  ['max-width']: `${this.maxWidth}px`
                }}
                class={styles.vmDialog}
                role="dialog"
                onKeydown={this.keydownHandler}
              >
                <div class={styles.vmBody} style={this.contentStyle}>
                  {guardUnspecified(this.$slots.header) && (
                    <div>{this.$slots.header}</div>
                  )}
                  {guardUnspecified(this.$slots.default) && (
                    <div>{this.$slots.default}</div>
                  )}
                  {guardUnspecified(this.$slots.footer) && (
                    <div>{this.$slots.footer}</div>
                  )}
                </div>
              </section>
            </div>
          </Portal>
        )}
      </div>
    );
  }
});
